import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft, Shield } from "lucide-react";
import { fetchPublicForm } from "../../lib/publicLeadApi";

const PROVIDER = "Entrena Tu Centro";

function isComplete(form) {
  return (
    !!form?.privacy_responsable &&
    !!form?.privacy_email &&
    !!form?.privacy_address &&
    !!form?.privacy_city
  );
}

export default function PublicPrivacyPolicy() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchPublicForm(slug)
      .then((data) => {
        if (!alive) return;
        if (!data) setLoadError("not_found");
        else if (!isComplete(data)) setLoadError("not_found");
        else setForm(data);
      })
      .catch(() => alive && setLoadError("error"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    document.title = "Política de privacidad · Entrena Tu Centro";
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            Página no encontrada
          </h1>
          <p className="text-sm text-gray-600">
            La política de privacidad de este buzón no está disponible.
          </p>
        </div>
      </div>
    );
  }

  const color = form.primary_color || "#10b981";
  const responsable = form.privacy_responsable;
  const email = form.privacy_email;
  const address = form.privacy_address;
  const city = form.privacy_city;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:py-12">
      <div className="max-w-2xl mx-auto">
        <Link
          to={`/l/${slug}`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft size={14} />
          Volver al formulario
        </Link>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="h-2" style={{ backgroundColor: color }} />

          <div className="p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${color}15` }}
              >
                <Shield size={22} style={{ color }} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                  Política de privacidad
                </h1>
                <p className="text-sm text-gray-500">
                  Protección de datos de carácter personal según la LOPD
                </p>
              </div>
            </div>

            <div className="text-gray-700 space-y-4 text-sm leading-relaxed">
              <p>
                <strong>{responsable}</strong>, en aplicación de la normativa
                vigente en materia de protección de datos de carácter personal,
                informa que los datos personales que se recogen a través de los
                formularios de este Sitio web se incluyen en los ficheros
                automatizados específicos de usuarios de los servicios de{" "}
                <strong>{responsable}</strong>.
              </p>

              <p>
                La recogida y tratamiento automatizado de los datos de carácter
                personal tiene como finalidad el mantenimiento de la relación
                comercial y el desempeño de tareas de información, formación,
                asesoramiento y otras actividades propias de{" "}
                <strong>{responsable}</strong>.
              </p>

              <p>
                Estos datos únicamente serán cedidos a aquellas entidades que
                sean necesarias con el único objetivo de dar cumplimiento a la
                finalidad anteriormente expuesta.
              </p>

              <p>
                <strong>{responsable}</strong> adopta las medidas necesarias
                para garantizar la seguridad, integridad y confidencialidad de
                los datos conforme a lo dispuesto en el Reglamento (UE) 2016/679
                del Parlamento Europeo y del Consejo, de 27 de abril de 2016,
                relativo a la protección de las personas físicas en lo que
                respecta al tratamiento de datos personales y a la libre
                circulación de los mismos. El usuario podrá en cualquier momento
                ejercitar los derechos de acceso, oposición, rectificación y
                cancelación reconocidos en el citado Reglamento (UE). El
                ejercicio de estos derechos puede realizarlo el propio usuario
                a través de email a:{" "}
                <a
                  href={`mailto:${email}`}
                  className="underline hover:no-underline"
                  style={{ color }}
                >
                  {email}
                </a>{" "}
                o en la dirección: {address} en {city}.
              </p>

              <p>
                El usuario manifiesta que todos los datos facilitados por él son
                ciertos y correctos, y se compromete a mantenerlos actualizados,
                comunicando los cambios a <strong>{responsable}</strong>.
              </p>

              <Section title="Finalidad del tratamiento de los datos personales">
                <p>
                  En <strong>{responsable}</strong>, trataremos tus datos
                  personales recabados a través de este Sitio Web con las
                  siguientes finalidades:
                </p>
                <ol className="list-decimal pl-5 space-y-1 mt-2">
                  <li>
                    En caso de contratación de los bienes y servicios ofertados
                    a través de <strong>{responsable}</strong>, para mantener la
                    relación contractual, así como la gestión, administración,
                    información, prestación y mejora del servicio.
                  </li>
                  <li>
                    Envío de información solicitada a través de los formularios
                    dispuestos en <strong>{responsable}</strong>.
                  </li>
                  <li>
                    Remitir boletines (newsletters), así como comunicaciones
                    comerciales de promociones y/o publicidad de{" "}
                    <strong>{responsable}</strong> y del sector.
                  </li>
                </ol>
                <p className="mt-3">
                  Te recordamos que puedes oponerte al envío de comunicaciones
                  comerciales por cualquier vía y en cualquier momento,
                  remitiendo un correo electrónico a la dirección indicada
                  anteriormente. Los campos de dichos registros son de
                  cumplimentación obligatoria, siendo imposible realizar las
                  finalidades expresadas si no se aportan esos datos.
                </p>
                <p className="mt-3">
                  Los datos personales proporcionados se conservarán mientras
                  se mantenga la relación comercial o no solicites su supresión
                  y durante el plazo por el cuál pudieran derivarse
                  responsabilidades legales por los servicios prestados.
                </p>
              </Section>

              <Section title="Legitimación">
                <p>
                  El tratamiento de tus datos se realiza con las siguientes
                  bases jurídicas que legitiman el mismo:
                </p>
                <ol className="list-decimal pl-5 space-y-1 mt-2">
                  <li>
                    La solicitud de información y/o la contratación de los
                    servicios de <strong>{responsable}</strong>, cuyos términos
                    y condiciones se pondrán a tu disposición en todo caso, de
                    forma previa a una eventual contratación.
                  </li>
                  <li>
                    El consentimiento libre, específico, informado e inequívoco,
                    en tanto que te informamos poniendo a tu disposición la
                    presente política de privacidad que, tras la lectura de la
                    misma, en caso de estar conforme, puedes aceptar mediante
                    una declaración o una clara acción afirmativa, como el
                    marcado de una casilla dispuesta al efecto. En caso de que
                    no nos facilites tus datos o lo hagas de forma errónea o
                    incompleta, no podremos atender tu solicitud, resultando del
                    todo imposible proporcionarte la información solicitada o
                    llevar a cabo la contratación de los servicios.
                  </li>
                </ol>
                <p className="mt-3">
                  <strong>Destinatarios:</strong> Los datos no se comunicarán a
                  ningún tercero ajeno a <strong>{responsable}</strong>, salvo
                  obligación legal.
                </p>
              </Section>

              <Section title="Datos recopilados por usuarios de los servicios">
                <p>
                  En los casos en que el usuario incluya ficheros con datos de
                  carácter personal en los servidores de alojamiento compartido,{" "}
                  <strong>{responsable}</strong> no se hace responsable del
                  incumplimiento por parte del usuario del RGPD.
                </p>
              </Section>

              <Section title="Retención de datos en conformidad a la LSSI">
                <p>
                  <strong>{responsable}</strong> informa de que, como prestador
                  de servicio de alojamiento de datos y en virtud de lo
                  establecido en la Ley 34/2002 de 11 de julio de Servicios de
                  la Sociedad de la Información y de Comercio Electrónico
                  (LSSI), retiene por un periodo máximo de 12 meses la
                  información imprescindible para identificar el origen de los
                  datos alojados y el momento en que se inició la prestación del
                  servicio. La retención de estos datos no afecta al secreto de
                  las comunicaciones y sólo podrán ser utilizados en el marco de
                  una investigación criminal o para la salvaguardia de la
                  seguridad pública, poniéndose a disposición de los jueces y/o
                  tribunales o del Ministerio que así los requiera. La
                  comunicación de datos a las Fuerzas y Cuerpos del Estado se
                  hará en virtud a lo dispuesto en la normativa sobre protección
                  de datos personales.
                </p>
                <p className="mt-3">
                  <strong>{responsable}</strong> es titular de todos los
                  derechos de autor, propiedad intelectual, industrial, "know
                  how" y cuantos otros derechos guardan relación con los
                  contenidos de este sitio web y los servicios ofertados en el
                  mismo, así como de los programas necesarios para su
                  implementación y la información relacionada. No se permite la
                  reproducción, publicación y/o uso no estrictamente privado de
                  los contenidos, totales o parciales, de este sitio web sin el
                  consentimiento previo y por escrito.
                </p>
                <p className="mt-3">
                  El usuario debe respetar los programas de terceros puestos a
                  su disposición por <strong>{responsable}</strong>, aun siendo
                  gratuitos y/o de disposición pública.{" "}
                  <strong>{responsable}</strong> dispone de los derechos de
                  explotación y propiedad intelectual necesarios del software.
                  El usuario no adquiere derecho alguno o licencia por el
                  servicio contratado, sobre el software necesario para la
                  prestación del servicio, ni tampoco sobre la información
                  técnica de seguimiento del servicio, excepción hecha de los
                  derechos y licencias necesarios para el cumplimiento de los
                  servicios contratados y únicamente durante la duración de los
                  mismos. Para toda actuación que exceda del cumplimiento del
                  contrato, el usuario necesitará autorización por escrito por
                  parte de <strong>{responsable}</strong>, quedando prohibido al
                  usuario acceder, modificar, visualizar la configuración,
                  estructura y ficheros de los servidores propiedad de{" "}
                  <strong>{PROVIDER}</strong>, asumiendo la responsabilidad
                  civil y penal derivada de cualquier incidencia que se pudiera
                  producir en los servidores y sistemas de seguridad como
                  consecuencia directa de una actuación negligente o maliciosa
                  por su parte.
                </p>
              </Section>

              <Section title="Usos prohibidos">
                <p>
                  Se prohíbe el uso contrario a la legislación sobre propiedad
                  intelectual de los servicios prestados por{" "}
                  <strong>{responsable}</strong> y, en particular de:
                </p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>
                    La utilización que resulte contraria a las leyes españolas o
                    que infrinja los derechos de terceros.
                  </li>
                  <li>
                    La publicación o la transmisión de cualquier contenido que,
                    a juicio de <strong>{responsable}</strong>, resulte
                    violento, obsceno, abusivo, ilegal, racial, xenófobo o
                    difamatorio.
                  </li>
                  <li>
                    Los cracks, números de serie de programas o cualquier otro
                    contenido que vulnere derechos de la propiedad intelectual
                    de terceros.
                  </li>
                  <li>
                    La recogida y/o utilización de datos personales de otros
                    usuarios sin su consentimiento expreso o contraviniendo lo
                    dispuesto en Reglamento (UE) 2016/679 del Parlamento Europeo
                    y del Consejo, de 27 de abril de 2016, relativo a la
                    protección de las personas físicas en lo que respecta al
                    tratamiento de datos personales y a la libre circulación de
                    los mismos.
                  </li>
                  <li>
                    La utilización del servidor de correo del dominio y de las
                    direcciones de correo electrónico para el envío de correo
                    masivo no deseado.
                  </li>
                </ul>
              </Section>

              <Section title="Responsabilidad del usuario">
                <p>
                  El usuario tiene toda la responsabilidad sobre el contenido de
                  su web, la información transmitida y almacenada, los enlaces
                  de hipertexto, las reivindicaciones de terceros y las acciones
                  legales en referencia a propiedad intelectual, derechos de
                  terceros y protección de menores.
                </p>
                <p className="mt-3">
                  El usuario es responsable respecto a las leyes y reglamentos
                  en vigor y las reglas que tienen que ver con el funcionamiento
                  del servicio online, comercio electrónico, derechos de autor,
                  mantenimiento del orden público, así como principios
                  universales de uso de Internet.
                </p>
                <p className="mt-3">
                  El usuario indemnizará a <strong>{responsable}</strong> por
                  los gastos que generará la imputación de{" "}
                  <strong>{responsable}</strong> en alguna causa cuya
                  responsabilidad fuera atribuible al usuario, incluidos
                  honorarios y gastos de defensa jurídica, incluso en el caso de
                  una decisión judicial no definitiva.
                </p>
              </Section>

              <Section title="Copias de seguridad">
                <p>
                  <strong>{responsable}</strong> realiza copias de seguridad de
                  los contenidos alojados en sus servidores, sin embargo no se
                  responsabiliza de la pérdida o el borrado accidental de los
                  datos por parte de los usuarios. De igual manera, no garantiza
                  la reposición total de los datos borrados por los usuarios, ya
                  que los citados datos podrían haber sido suprimidos y/o
                  modificados durante el periodo del tiempo transcurrido desde
                  la última copia de seguridad.
                </p>
                <p className="mt-3">
                  Los servicios ofertados, excepto los servicios específicos de
                  backup, no incluyen la reposición de los contenidos
                  conservados en las copias de seguridad realizadas por{" "}
                  <strong>{responsable}</strong> cuando esta pérdida sea
                  imputable al usuario; en este caso, se determinará una tarifa
                  acorde a la complejidad y volumen de la recuperación, siempre
                  previa aceptación del usuario.
                </p>
                <p className="mt-3">
                  La reposición de datos borrados sólo está incluida en el
                  precio del servicio cuando la pérdida del contenido sea debida
                  a causas atribuibles a <strong>{responsable}</strong>.
                </p>
              </Section>

              <Section title="Comunicaciones comerciales">
                <p>
                  En aplicación de la LSSI, <strong>{responsable}</strong> no
                  enviará comunicaciones publicitarias o promocionales por
                  correo electrónico u otro medio de comunicación electrónica
                  equivalente que previamente no hubieran sido solicitadas o
                  expresamente autorizadas por los destinatarios de las mismas.
                </p>
                <p className="mt-3">
                  En el caso de usuarios con los que exista una relación
                  contractual previa, <strong>{responsable}</strong> sí está
                  autorizado al envío de comunicaciones comerciales referentes a
                  productos o servicios de <strong>{responsable}</strong> que
                  sean similares a los que inicialmente fueron objeto de
                  contratación con el cliente. En todo caso, el usuario, tras
                  acreditar su identidad, podrá solicitar que no se le haga
                  llegar más información comercial a través de los canales de
                  Atención al Cliente.
                </p>
              </Section>
            </div>

            <p className="mt-8 pt-5 border-t border-gray-100 text-xs text-gray-400 text-center">
              Última actualización:{" "}
              {new Date().toLocaleDateString("es-ES", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pt-2">
      <h2 className="text-base font-semibold text-gray-900 mb-2">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
